import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import {
  classOf,
  type PeriodicElement,
} from "@/lib/tool-kit/chem-reference/periodic";

export interface ListStrings {
  columnNumber: string;
  columnSymbol: string;
  columnName: string;
  columnWeight: string;
  columnGroup: string;
  columnPeriod: string;
  columnBlock: string;
  columnCategory: string;
  columnElectronegativity: string;
  columnDiscovered: string;
  groupNone: string;
  ancient: string;
  dash: string;
  listCaption: string;
  classLabels: Record<string, string>;
}

/** Every element as a table row, for reading, searching and screen readers. */
export function PeriodicList({
  elements,
  selected,
  strings,
  fmt,
  onSelect,
}: {
  elements: readonly PeriodicElement[];
  selected: number;
  strings: ListStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  onSelect: (z: number) => void;
}) {
  const heads = [
    strings.columnNumber,
    strings.columnSymbol,
    strings.columnName,
    strings.columnWeight,
    strings.columnGroup,
    strings.columnPeriod,
    strings.columnBlock,
    strings.columnCategory,
    strings.columnElectronegativity,
    strings.columnDiscovered,
  ];
  return (
    <div className="max-h-[32rem] overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {strings.listCaption}
        </caption>
        <thead>
          <tr>
            {heads.map((head) => (
              <th className={TH} key={head} scope="col">
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {elements.map((element) => {
            const weight = fmt(element.weight, { maximumFractionDigits: 4 });
            return (
              <tr
                className={element.z === selected ? "bg-primary/5" : undefined}
                key={element.z}
              >
                <td className={TD}>{element.z}</td>
                <td className={`${TD} font-semibold`}>{element.symbol}</td>
                <th className={`${TD} text-left font-normal`} scope="row">
                  <button
                    aria-pressed={element.z === selected}
                    className="text-left text-primary underline-offset-2 hover:underline"
                    onClick={() => onSelect(element.z)}
                    type="button"
                  >
                    {element.name}
                  </button>
                </th>
                <td className={TD}>
                  {element.massNumber ? `[${weight}]` : weight}
                </td>
                <td className={TD}>{element.group ?? strings.groupNone}</td>
                <td className={TD}>{element.period}</td>
                <td className={TD}>{element.block}</td>
                <td className={TD}>
                  {strings.classLabels[classOf(element, "category")]}
                </td>
                <td className={TD}>
                  {element.electronegativity === null
                    ? strings.dash
                    : fmt(element.electronegativity, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                </td>
                <td className={TD}>
                  {element.discovered === null
                    ? strings.ancient
                    : element.discovered}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
